import React, { Component, useState, useEffect, memo } from "react";
import ReactTable from "react-table";
import "react-table/react-table.css";
import {
  Input,
  CustomInput,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  FormGroup,
  Label,
  Card,
  CardBody,
  Badge,
  Collapse,
} from "reactstrap";
import Creatable from "../../../components/common/CreatableSelect";
import { AddCommaNumber, CleanPrice } from "../../../helpers/Utils";
import PriceInput from "../../../components/common/PriceInput";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import { injectIntl } from "react-intl";
import { Colxx } from "../../../components/common/CustomBootstrap";
import RecipeBuilder from "./RecipeBuilder";
import StockInfo from "./StockInfo";
import SizeAndTopping from "./SizeAndTopping";
import IceAndSugar from "./IceAndSugar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

// --- Cell Components ---

const EditableCell = memo(
  ({
    value,
    onChange,
    onBlur,
    onKeyDown,
    type = "text",
    placeholder = "",
    disabled = false,
    ...rest
  }) => {
    const [localValue, setLocalValue] = useState(value || "");

    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    const handleChange = (e) => setLocalValue(e.target.value);

    const handleBlur = () => {
      if (localValue !== value) onChange(localValue);
    };

    return (
      <Input
        type={type}
        bsSize="sm"
        value={localValue}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onBlur={handleBlur}
        disabled={disabled}
        style={type === "number" ? { textAlign: "right" } : {}}
        {...rest}
      />
    );
  },
);

const NumberEditableCell = memo(
  ({ value, onChange, placeholder = "0", disabled = false }) => {
    const [displayValue, setDisplayValue] = useState(
      value ? AddCommaNumber(value) : "",
    );

    useEffect(() => {
      setDisplayValue(value ? AddCommaNumber(value) : "");
    }, [value]);

    const handleChange = (e) => {
      const inputValue = e.target.value;
      const rawNumber = inputValue.replace(/\./g, "");
      if (rawNumber === "" || /^\d+$/.test(rawNumber)) {
        setDisplayValue(rawNumber ? AddCommaNumber(rawNumber) : "");
      }
    };

    const handleBlur = () => {
      const rawNumber = displayValue.replace(/\./g, "");
      if (rawNumber !== CleanPrice(value)) {
        onChange(rawNumber);
      }
    };

    return (
      <Input
        type="text"
        bsSize="sm"
        value={displayValue}
        placeholder={placeholder}
        style={{ textAlign: "right", fontWeight: "bold" }}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
      />
    );
  },
);

const CategorySelectCell = memo(({ value, options, onChange }) => {
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    let option = options.find((c) => c.label === value || c.value === value);

    if (typeof value === "object" && value !== null) {
      option = options.find((c) => c.value === value.id) || {
        label: value.name,
        value: value.id,
      };
    }

    if (!option && value && typeof value === "string") {
      option = { label: value, value: value };
    }
    setSelectedOption(option || null);
  }, [value, options]);

  const handleChange = (opt) => {
    setSelectedOption(opt);
    onChange(opt);
  };

  return (
    <div style={{ width: "100%" }}>
      <Creatable
        className="react-select"
        classNamePrefix="react-select"
        options={options}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        value={selectedOption}
        onChange={handleChange}
        placeholder="Chọn..."
        formatCreateLabel={(inputValue) => `Tạo mới: "${inputValue}"`}
      />
    </div>
  );
});

class ContentMultiForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isModalOpenRecipe: false,
      isModalOpenVariant: false,
      editingIndex: null,
      editingData: null,
      // Variant settings for the modal
      masterVariantSettings: {
        price: "",
        promo_price: "",
        date_start: null,
        date_end: null,
        quantity: "",
        cost_price: "",
      },
    };
    this.scrollRef = null;
    this.fileInputRef = React.createRef();
    this.currentUploadRowIndex = null;
  }

  // --- Modal Handlers ---

  toggleRecipeModal = () => {
    this.setState((prevState) => ({
      isModalOpenRecipe: !prevState.isModalOpenRecipe,
      editingData: !prevState.isModalOpenRecipe ? null : prevState.editingData,
    }));
  };

  toggleVariantModal = () => {
    this.setState((prevState) => ({
      isModalOpenVariant: !prevState.isModalOpenVariant,
      editingData: !prevState.isModalOpenVariant ? null : prevState.editingData,
      masterVariantSettings: {
        price: "",
        promo_price: "",
        date_start: null,
        date_end: null,
        quantity: "",
        cost_price: "",
      },
    }));
  };

  openRecipeModal = (index) => {
    const { data } = this.props;
    if (!data || !data[index]) return;
    this.setState({
      editingIndex: index,
      editingData: JSON.parse(JSON.stringify(data[index])), // Deep clone
      isModalOpenRecipe: true,
    });
  };

  openVariantModal = (index) => {
    const { data } = this.props;
    if (!data || !data[index]) return;
    this.setState({
      editingIndex: index,
      editingData: JSON.parse(JSON.stringify(data[index])), // Deep clone
      isModalOpenVariant: true,
    });
  };

  handleModalDataChange = (value, field) => {
    this.setState((prevState) => ({
      editingData: {
        ...prevState.editingData,
        [field]: value,
      },
    }));
  };

  handleModalVariantChange = (value, variantId, field) => {
    const cleanValue =
      field === "price" ||
      field === "promo_price" ||
      field === "quantity" ||
      field === "cost_price"
        ? CleanPrice(value)
        : value;

    this.setState((prevState) => {
      const variant = [...(prevState.editingData.variant || [])];
      const index = variant.findIndex((v) => v.id === variantId);
      if (index !== -1) {
        variant[index] = { ...variant[index], [field]: cleanValue };
      }
      return {
        editingData: { ...prevState.editingData, variant },
      };
    });
  };

  handleModalGenerateVariants = () => {
    const { editingData } = this.state;
    if (!editingData) return;

    const sizeOptions = editingData.size || [];
    const colorOptions = editingData.color || [];
    const materialOptions = editingData.material || [];

    const effectiveSizes =
      sizeOptions.length > 0 ? sizeOptions : [{ label: "", value: "no-size" }];
    const effectiveColors =
      colorOptions.length > 0
        ? colorOptions
        : [{ label: "", value: "no-color" }];
    const effectiveMaterials =
      materialOptions.length > 0
        ? materialOptions
        : [{ label: "", value: "no-material" }];

    const newVariant = effectiveSizes.flatMap((s) =>
      effectiveColors.flatMap((c) =>
        effectiveMaterials.map((m, idx) => {
          const parts = [s.label, c.label, m.label].filter(
            (p) => p && p.trim() !== "",
          );
          const name = parts.join(" - ");
          return {
            id: `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: name,
            size: s,
            color: c,
            material: m,
            sku: editingData.sku ? `${editingData.sku}.${idx + 1}` : "",
            quantity: editingData.stock_quantity || "1",
            price: editingData.price || "0",
            cost_price: editingData.stock_cost_price || "0",
            promo_price: "",
            isOpen: false,
            attributes: { size: s, color: c, material: m },
          };
        }),
      ),
    );

    this.setState((prevState) => ({
      editingData: {
        ...prevState.editingData,
        variant: newVariant,
      },
    }));
  };

  handleModalDeleteVariant = (id) => {
    this.setState((prevState) => ({
      editingData: {
        ...prevState.editingData,
        variant: (prevState.editingData.variant || []).filter(
          (v) => v.id !== id,
        ),
      },
    }));
  };

  handleModalMasterSettingsChange = (value, field) => {
    this.setState((prevState) => ({
      masterVariantSettings: {
        ...prevState.masterVariantSettings,
        [field]: value,
      },
    }));
  };

  applyMasterSettingsToAll = () => {
    const { masterVariantSettings, editingData } = this.state;
    if (!editingData || !editingData.variant) return;

    const newVariant = editingData.variant.map((v) => ({
      ...v,
      price: masterVariantSettings.price
        ? CleanPrice(masterVariantSettings.price)
        : v.price,
      promo_price: masterVariantSettings.promo_price
        ? CleanPrice(masterVariantSettings.promo_price)
        : v.promo_price,
      cost_price: masterVariantSettings.cost_price
        ? CleanPrice(masterVariantSettings.cost_price)
        : v.cost_price,
      quantity: masterVariantSettings.quantity
        ? CleanPrice(masterVariantSettings.quantity)
        : v.quantity,
      date_start: masterVariantSettings.date_start || v.date_start,
      date_end: masterVariantSettings.date_end || v.date_end,
    }));

    this.setState((prevState) => ({
      editingData: {
        ...prevState.editingData,
        variant: newVariant,
      },
    }));
  };

  toggleAccordionVariations = (id) => {
    this.setState((prevState) => {
      const variant = (prevState.editingData.variant || []).map((v) =>
        v.id === id ? { ...v, isOpen: !v.isOpen } : v,
      );
      return {
        editingData: { ...prevState.editingData, variant },
      };
    });
  };

  saveModalChanges = () => {
    const { editingIndex, editingData } = this.state;
    const { onUpdateData } = this.props;
    if (onUpdateData && editingData) {
      onUpdateData([editingData]);
    }
    this.setState({
      isModalOpenRecipe: false,
      isModalOpenVariant: false,
      editingIndex: null,
      editingData: null,
    });
  };

  setScrollRef = (el) => {
    this.scrollRef = el;
  };

  componentWillUnmount() {}

  handleCellChange = (index, field, value) => {
    const { data, onUpdateData } = this.props;
    if (!data || !data[index]) return;

    const updatedItem = { ...data[index], [field]: value };

    if (onUpdateData) {
      onUpdateData([updatedItem]);
    }
  };

  handleCellChangeMulti = (index, changes) => {
    const { data, onUpdateData } = this.props;
    if (!data || !data[index]) return;

    const updatedItem = { ...data[index], ...changes };

    if (onUpdateData) {
      onUpdateData([updatedItem]);
    }
  };

  handleDeleteRow = (index) => {
    const { data, onDelete } = this.props;
    if (data && data[index] && onDelete) {
      onDelete(data[index]);
    }
  };

  handleCategoryChange = (index, opt) => {
    const { data, onUpdateData } = this.props;
    if (!data || !data[index]) return;

    const updatedItem = {
      ...data[index],
      category: opt
        ? {
            ...data[index].category,
            id: opt.__isNew__ ? opt.label : opt.value,
            name: opt.label,
          }
        : data[index].category,
      category_id: opt ? (opt.__isNew__ ? opt.label : opt.value) : null,
    };

    if (onUpdateData) {
      onUpdateData([updatedItem]);
    }
  };

  triggerImageUpload = (rowIndex) => {
    this.currentUploadRowIndex = rowIndex;
    if (this.fileInputRef.current) {
      this.fileInputRef.current.click();
    }
  };

  handleImageUpload = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || this.currentUploadRowIndex === null)
      return;

    const currentImages = this.props.data[this.currentUploadRowIndex].image;
    let newImages = [];
    if (Array.isArray(currentImages)) {
      newImages = [...currentImages];
    } else if (currentImages) {
      newImages = [currentImages];
    }

    let processedCount = 0;
    const totalFiles = files.length;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newImages.push(reader.result);
        processedCount++;

        if (processedCount === totalFiles) {
          this.handleCellChange(this.currentUploadRowIndex, "image", newImages);
          this.currentUploadRowIndex = null;
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset file input
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  };

  removeImage = (rowIndex, imageIndex) => {
    const currentImages = this.props.data[rowIndex].image;
    if (Array.isArray(currentImages)) {
      const newImages = [...currentImages];
      newImages.splice(imageIndex, 1);
      this.handleCellChange(rowIndex, "image", newImages);
    }
  };

  getColumns = () => {
    const {
      categories,
      page = 1,
      pageSize = 15,
      selectedItems,
      toggleItem,
      onSelectAll,
      data,
      business_type,
    } = this.props;
    const { messages } = this.props.intl;
    const isFnB = business_type === "cafe" || business_type === "restaurant";

    // Transform categories for Select
    const categoryOptions = (categories || []).map((c) => ({
      label: c.value || c.name,
      value: c.id,
    }));

    let allColumns = [
      // Checkbox column
      {
        Header: () => {
          return (
            <div className="d-flex justify-content-center w-100">
              <CustomInput
                type="checkbox"
                id="checkAllProduct"
                label=""
                className="cursor-pointer"
                style={{ cursor: "pointer" }}
                checked={
                  selectedItems &&
                  selectedItems.length > 0 &&
                  data &&
                  selectedItems.length >= data.length
                }
                onChange={() => onSelectAll && onSelectAll(true)}
              />
            </div>
          );
        },
        accessor: "id",
        width: 50,
        sortable: false,
        Cell: (cellProps) => {
          return (
            <div className="d-flex justify-content-center w-100">
              <CustomInput
                type="checkbox"
                id={`check_product_${cellProps.value}`}
                label=""
                className="cursor-pointer"
                style={{ cursor: "pointer" }}
                checked={
                  selectedItems && selectedItems.includes(cellProps.value)
                }
                onChange={() => toggleItem && toggleItem(cellProps.value)}
              />
            </div>
          );
        },
      },
      // STT column
      // {
      //   Header: "#",
      //   width: 40,
      //   style: {
      //     display: "flex",
      //     justifyContent: "center",
      //     alignItems: "center",
      //   },
      //   Cell: (row) => (
      //     <div className="text-center">
      //       {(page - 1) * pageSize + row.index + 1}
      //     </div>
      //   ),
      // },
      // Name column
      {
        Header: messages["product.multi.name"],
        accessor: "name",
        minWidth: 180,
        Cell: (props) => (
          <EditableCell
            value={props.value}
            onChange={(val) => this.handleCellChange(props.index, "name", val)}
          />
        ),
      },
      // Barcode column
      {
        Header: messages["product.multi.barcode"],
        accessor: "barcode",
        width: 150,
        Cell: (props) => (
          <EditableCell
            value={props.value}
            onChange={(val) =>
              this.handleCellChange(props.index, "barcode", val)
            }
          />
        ),
      },
      // SKU column
      {
        Header: messages["product.multi.sku"],
        accessor: "sku",
        style: { color: "#555" },
        width: 120,
        Cell: (props) => (
          <div className="d-flex align-items-center h-100 pl-2">
            {props.value}
          </div>
        ),
      },
      // Unit column
      {
        Header: messages["product.multi.unit"] || "Đơn vị",
        accessor: "unit",
        width: 100,
        Cell: (props) => {
          const { data_stocks, business_type } = this.props;
          const isFnB =
            business_type === "cafe" || business_type === "restaurant";

          let displayUnit = props.value;
          if (!displayUnit && data_stocks && data_stocks.length > 0) {
            const relatedStock = data_stocks.find(
              (s) =>
                s.name === props.original.name || s.sku === props.original.sku,
            );
            if (relatedStock) displayUnit = relatedStock.unit;
          }

          return (
            <EditableCell
              value={displayUnit}
              placeholder={isFnB ? "ly, phần..." : "cái, chiếc..."}
              onChange={(val) =>
                this.handleCellChange(props.index, "unit", val)
              }
            />
          );
        },
      },
      // Category column
      {
        Header: messages["product.multi.category"],
        accessor: "category",
        width: 150,
        Cell: (props) => (
          <CategorySelectCell
            value={props.value ? props.value.name : ""}
            options={categoryOptions}
            onChange={(opt) => this.handleCategoryChange(props.index, opt)}
          />
        ),
      },

      // Price column
      {
        Header: messages["product.multi.price"],
        accessor: "price",
        width: 90,
        Cell: (props) => {
          const row = props.original;
          const price = parseFloat(row.price || 0);
          const priceSale = parseFloat(row.price_sale || 0);
          const isSale = priceSale > 0 && priceSale < price;

          return (
            <div style={{ position: "relative" }}>
              <NumberEditableCell
                value={props.value}
                onChange={(val) => {
                  const p = parseFloat(val || 0);
                  const ps = parseFloat(row.price_sale || 0);
                  this.handleCellChangeMulti(props.index, {
                    price: val,
                    active_sale: ps > 0 && ps < p,
                  });
                }}
                style={{
                  textAlign: "right",
                  textDecoration: isSale ? "line-through" : "none",
                  color: isSale ? "#999" : "inherit",
                }}
              />
              {isSale && (
                <div
                  style={{
                    position: "absolute",
                    top: "-15px",
                    right: 0,
                    fontSize: "10px",
                    color: "red",
                    fontWeight: "bold",
                  }}
                >
                  Sale: {AddCommaNumber(priceSale)}
                </div>
              )}
            </div>
          );
        },
      },
      // Price sale column
      {
        Header: messages["product.multi.price_sale"],
        accessor: "price_sale",
        width: 90,
        Cell: (props) => (
          <NumberEditableCell
            value={props.value}
            placeholder="0"
            onChange={(val) => {
              const ps = parseFloat(val || 0);
              const p = parseFloat(props.original.price || 0);
              this.handleCellChangeMulti(props.index, {
                price_sale: val,
                active_sale: ps > 0 && ps < p,
              });
            }}
          />
        ),
      },
      // Tax rate column
      {
        Header: messages["product.tax-rate"] || "Thuế (%)",
        accessor: "tax_rate",
        width: 80,
        Cell: (props) => (
          <NumberEditableCell
            value={props.value ?? 0}
            placeholder="0"
            onChange={(val) => {
              this.handleCellChange(props.index, "tax_rate", val);
            }}
          />
        ),
      },
      // Recipe/Định lượng column (FnB only)
      {
        Header: messages["product.multi.recipe"] || "Định lượng",
        id: "recipe",
        width: 100,
        headerStyle: { textAlign: "center" },
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        Cell: (props) => {
          const { ingredients, make_product } = props.original;
          const count =
            (ingredients?.length || 0) + (make_product?.length || 0);
          return (
            <div className="text-center">
              <Button
                color="outline-info"
                size="xs"
                style={{ padding: "0.4rem 0.5rem", fontSize: "11px" }}
                onClick={() => this.openRecipeModal(props.index)}
              >
                {count > 0
                  ? `${count} NL`
                  : messages["product.multi.recipe"] || "Định lượng"}
              </Button>
            </div>
          );
        },
      },
      // Variant/Biến thể column
      {
        Header: messages["product.multi.variant"] || "Biến thể",
        id: "variant",
        width: 100,
        headerStyle: { textAlign: "center" },
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        Cell: (props) => {
          const { size, topping, variant, color, material } = props.original;
          let count = 0;

          if (isFnB) {
            count = (size?.length || 0) + (topping?.length || 0);
          } else {
            count =
              (variant?.length || 0) +
              (color?.length || 0) +
              (material?.length || 0);
          }

          return (
            <div className="text-center">
              <Button
                color="outline-primary"
                size="xs"
                style={{ padding: "0.4rem 0.5rem", fontSize: "11px" }}
                onClick={() => this.openVariantModal(props.index)}
              >
                {count > 0
                  ? `${count} ${messages["modal.product.variant"] || "BT"}`
                  : messages["product.multi.variant"] || "Biến thể"}
              </Button>
            </div>
          );
        },
      },
      // Image column
      {
        Header: messages["product.multi.image"],
        accessor: "image",
        width: 120,
        headerStyle: { textAlign: "center" },
        Cell: (props) => {
          const images = (
            Array.isArray(props.value)
              ? props.value
              : props.value
                ? [props.value]
                : []
          ).filter(
            (img) =>
              img &&
              String(img).trim() !== "" &&
              String(img) !== "null" &&
              String(img) !== "undefined" &&
              !String(img).includes("default") &&
              !String(img).includes("placeholder"),
          );
          const hasImages = images.length > 0;
          return (
            <div
              className={`d-flex align-items-center ${
                !hasImages ? "justify-content-center" : ""
              }`}
              style={{
                height: "100%",
                width: "100%",
                overflowX: "auto",
                position: "relative",
              }}
            >
              <div
                className="d-flex"
                style={hasImages ? { paddingRight: "40px" } : {}}
              >
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className="position-relative mr-2"
                    style={{ minWidth: "30px" }}
                  >
                    <img
                      src={img}
                      alt=""
                      onError={(e) => {
                        e.target.style.display = "none";
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = "none";
                        }
                      }}
                      style={{
                        height: "30px",
                        width: "30px",
                        objectFit: "cover",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }}
                    />
                    <span
                      className="position-absolute"
                      style={{
                        top: "-2px",
                        right: "-6px",
                        cursor: "pointer",
                        background: "#ff0000",
                        color: "#fff",
                        borderRadius: "50%",
                        width: "16px",
                        height: "16px",
                        fontSize: "10px",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 10,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        this.removeImage(props.index, idx);
                      }}
                      title="Xóa ảnh"
                    >
                      x
                    </span>
                  </div>
                ))}
              </div>

              <div
                className="text-center cursor-pointer ml-1"
                onClick={() => this.triggerImageUpload(props.index)}
                style={{
                  width: "28px",
                  height: "28px",
                  border: "1px dashed #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  flexShrink: 0,
                  marginTop: "0px",
                  position: hasImages ? "sticky" : "static",
                  right: 0,
                  background: "#fff",
                  zIndex: 5,
                  boxShadow: hasImages ? "-2px 0 5px rgba(0,0,0,0.1)" : "none",
                }}
                title="Thêm ảnh"
              >
                <i
                  className="simple-icon-plus text-muted"
                  style={{ fontSize: "10px" }}
                />
              </div>
            </div>
          );
        },
      },
      // Hot column
      {
        Header: messages["product.multi.hot"],
        accessor: "bestter",
        width: 60,
        headerStyle: { textAlign: "center" },
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        Cell: (props) => (
          <div className="text-center">
            <CustomInput
              type="checkbox"
              id={`bestter-${props.index}`}
              checked={props.value}
              onChange={(e) =>
                this.handleCellChange(props.index, "bestter", e.target.checked)
              }
            />
          </div>
        ),
      },
      // Soft hide column
      {
        Header: messages["product.multi.soft_hide"],
        accessor: "softHide",
        width: 50,
        headerStyle: { textAlign: "center" },
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        Cell: (props) => (
          <div className="text-center">
            <CustomInput
              type="checkbox"
              id={`hide-${props.index}`}
              checked={props.value}
              onChange={(e) =>
                this.handleCellChange(props.index, "softHide", e.target.checked)
              }
            />
          </div>
        ),
      },
      // Delete column
      {
        Header: "",
        id: "action",
        width: 40,
        headerStyle: { textAlign: "center" },
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        Cell: (props) => (
          <div className="text-center">
            <i
              className="simple-icon-trash text-danger cursor-pointer"
              onClick={() => this.handleDeleteRow(props.index)}
            />
          </div>
        ),
        // Cell: (props) => (
        //   <div className="text-center">
        //     <Button
        //       color="danger"
        //       size="xs"
        //       outline
        //       style={{
        //         padding: "0.2rem 0.4rem",
        //         display: "flex",
        //         alignItems: "center",
        //         justifyContent: "center",
        //       }}
        //       onClick={() => this.handleDeleteRow(props.index)}
        //       title={messages["general.delete"] || "Xóa"}
        //     >
        //       <i className="simple-icon-trash" />
        //     </Button>
        //   </div>
        // ),
      },
    ];

    // Filter columns based on business_type (matching ModalAddProducts logic)
    if (isFnB) {
      // FnB: Hide barcode and unit column
      const columnsToHide = ["barcode", "unit"];
      allColumns = allColumns.filter(
        (col) =>
          !columnsToHide.includes(col.accessor) &&
          !columnsToHide.includes(col.id),
      );
    } else {
      // Non-FnB: Hide recipe column
      allColumns = allColumns.filter((col) => col.id !== "recipe");
    }

    return allColumns;
  };

  render() {
    const { data, business_type, intl } = this.props;
    const messages = intl ? intl.messages : {};
    const isFnB = business_type === "cafe" || business_type === "restaurant";

    return (
      <div className="content-multi-form-container">
        {/* Hidden file input for image upload */}
        <input
          ref={this.fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={this.handleImageUpload}
        />
        <style>{`
          .content-multi-form-container .rt-thead.-header {
            box-shadow: none;
            border-bottom: 1px solid #f3f3f3;
          }
          .content-multi-form-container .rt-th {
            padding: 10px 5px;
            font-weight: 600;
            text-align: center;
            background: #f8f8f8;
            color: #333;
            border-right: 1px solid #f3f3f3;
          }
          .content-multi-form-container .rt-td {
            padding: 4px 5px;
            display: flex;
            align-items: center;
            border-bottom: 1px solid #f3f3f3;
            border-right: 1px solid #f3f3f3;
          }
           .content-multi-form-container .rt-tr,
           .content-multi-form-container .rt-td {
               outline: none !important;
           }
          .content-multi-form-container .rt-tr:hover {
            background: #fcfcfc;
          }
          .content-multi-form-container input.form-control-sm {
            height: 32px;
            font-size: 13px;
          }
          .content-multi-form-container .react-select .react-select__control {
            min-height: 32px;
          }
          .content-multi-form-container .custom-control {
              min-height: auto;
          }
          .content-multi-form-container .cursor-pointer {
            cursor: pointer;
          }
        `}</style>

        <PerfectScrollbar
          ref={this.setScrollRef}
          options={{ suppressScrollX: false, suppressScrollY: false }}
          style={{
            width: "100%",
            maxHeight: "70vh",
          }}
        >
          <div style={{ minWidth: "100%", width: "max-content" }}>
            <ReactTable
              data={data || []}
              columns={this.getColumns()}
              defaultPageSize={15}
              showPagination={false}
              minRows={1}
              className="-striped -highlight"
            />
          </div>
        </PerfectScrollbar>

        {/* --- Recipe Modal (Column 2 of UpdateProduct) --- */}
        <Modal
          isOpen={this.state.isModalOpenRecipe}
          toggle={this.toggleRecipeModal}
          size="lg"
          centered
        >
          <ModalHeader toggle={this.toggleRecipeModal}>
            {messages["product.multi.recipe"] || "Định lượng"}
          </ModalHeader>
          <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {this.state.editingData && (
              <>
                <div
                  className="mb-3 p-3 bg-light rounded"
                  style={{ borderLeft: "4px solid #145388" }}
                >
                  <Row>
                    <Colxx sm="12" md="4">
                      <p className="mb-1">
                        <strong>
                          {messages["product.multi.name"] || "Tên SP"}:
                        </strong>{" "}
                        <span className="text-primary font-weight-bold">
                          {this.state.editingData.name || "---"}
                        </span>
                      </p>
                    </Colxx>
                    <Colxx sm="6" md="4">
                      <p className="mb-1">
                        <strong>
                          {messages["product.multi.sku"] || "SKU"}:
                        </strong>{" "}
                        {this.state.editingData.sku || "---"}
                      </p>
                    </Colxx>
                    <Colxx sm="6" md="4">
                      <p className="mb-0">
                        <strong>
                          {messages["product.multi.price"] || "Giá bán"}:
                        </strong>{" "}
                        {AddCommaNumber(this.state.editingData.price || 0)}đ
                      </p>
                    </Colxx>
                  </Row>
                </div>
                <Row>
                  <Colxx xxs="12">
                    {business_type === "cafe" ||
                    business_type === "restaurant" ||
                    this.state.editingData.ingredients?.length > 0 ||
                    this.state.editingData.make_product?.length > 0 ? (
                      <RecipeBuilder
                        ingredients={
                          this.state.editingData.ingredients ||
                          this.state.editingData.make_product?.map(
                            (item, idx) => ({
                              id: `ing_${item.id || idx}`,
                              stock_id: item.stock?.id,
                              stock_name: item.stock?.name || "",
                              unit: item.stock?.unit || "",
                              quantity: item.count?.toString() || "",
                              price: item.stock?.avarage_price || 0,
                            }),
                          ) ||
                          []
                        }
                        data_stocks={this.props.data_stocks || []}
                        onChange={(ingredients) =>
                          this.handleModalDataChange(ingredients, "ingredients")
                        }
                        sellingPrice={
                          parseFloat(CleanPrice(this.state.editingData.price)) || 0
                        }
                        data_providers={this.props.data_providers || []}
                      />
                    ) : (
                      <StockInfo
                        title="Bổ sung nguyên liệu"
                        quantity={this.state.editingData.stock_quantity || ""}
                        costPrice={
                          this.state.editingData.stock_cost_price || ""
                        }
                        sellingPrice={this.state.editingData.price || ""}
                        onChangeQuantity={(val) =>
                          this.handleModalDataChange(val, "stock_quantity")
                        }
                        onChangeCostPrice={(val) =>
                          this.handleModalDataChange(val, "stock_cost_price")
                        }
                        quantityLabel="Tồn kho"
                        quantityDisabled={true}
                      />
                    )}
                  </Colxx>
                </Row>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.saveModalChanges}>
              {messages["general.done"] || "Lưu"}
            </Button>
            <Button color="secondary" onClick={this.toggleRecipeModal}>
              {messages["general.btn-cancel"] || "Hủy"}
            </Button>
          </ModalFooter>
        </Modal>

        {/* --- Variant Modal (Column 3 of UpdateProduct) --- */}
        <Modal
          isOpen={this.state.isModalOpenVariant}
          toggle={this.toggleVariantModal}
          size="xl"
          centered
        >
          <ModalHeader toggle={this.toggleVariantModal}>
            {messages["product.multi.variant"] || "Biến thể"}
          </ModalHeader>
          <ModalBody style={{ maxHeight: "80vh", overflowY: "auto" }}>
            {this.state.editingData && (
              <>
                <div
                  className="mb-3 p-3 bg-light rounded"
                  style={{ borderLeft: "4px solid #145388" }}
                >
                  <Row>
                    <Colxx sm="12" md="4">
                      <p className="mb-1">
                        <strong>
                          {messages["product.multi.name"] || "Tên SP"}:
                        </strong>{" "}
                        <span className="text-primary font-weight-bold">
                          {this.state.editingData.name || "---"}
                        </span>
                      </p>
                    </Colxx>
                    <Colxx sm="6" md="4">
                      <p className="mb-1">
                        <strong>
                          {messages["product.multi.sku"] || "SKU"}:
                        </strong>{" "}
                        {this.state.editingData.sku || "---"}
                      </p>
                    </Colxx>
                    <Colxx sm="6" md="4">
                      <p className="mb-0">
                        <strong>
                          {messages["product.multi.price"] || "Giá bán"}:
                        </strong>{" "}
                        {AddCommaNumber(this.state.editingData.price || 0)}đ
                      </p>
                    </Colxx>
                  </Row>
                </div>
                <Row>
                  <Colxx xxs="12">
                    {isFnB ? (
                      <Row>
                        <Colxx md="6">
                          <FormGroup className="mb-4">
                            <Label className="text-muted">
                              {this.state.editingData.sizeTitle || "Size"}
                            </Label>
                            <SizeAndTopping
                              onAdd={() => {
                                const sizes = [
                                  ...(this.state.editingData.size || []),
                                ];
                                const nextId =
                                  sizes.length > 0
                                    ? Math.max(...sizes.map((s) => s.id || 0)) +
                                      1
                                    : 1;
                                sizes.push({ id: nextId, name: "", value: 0 });
                                this.handleModalDataChange(sizes, "size");
                              }}
                              onChange={(val, type, id, field) => {
                                const sizes = (
                                  this.state.editingData.size || []
                                ).map((item) =>
                                  item.id === id
                                    ? {
                                        ...item,
                                        [field]:
                                          field === "value"
                                            ? val.toString().replace(/,/g, "")
                                            : val,
                                      }
                                    : item,
                                );
                                this.handleModalDataChange(sizes, "size");
                              }}
                              onRemove={(type, id) => {
                                const sizes = (
                                  this.state.editingData.size || []
                                ).filter((item) => item.id !== id);
                                this.handleModalDataChange(sizes, "size");
                              }}
                              name="size"
                              values={this.state.editingData.size || []}
                            />
                          </FormGroup>
                          <FormGroup className="mb-4">
                            <Label className="text-muted">
                              {this.state.editingData.toppingTitle || "Topping"}
                            </Label>
                            <SizeAndTopping
                              onAdd={() => {
                                const toppings = [
                                  ...(this.state.editingData.topping || []),
                                ];
                                const nextId =
                                  toppings.length > 0
                                    ? Math.max(
                                        ...toppings.map((t) => t.id || 0),
                                      ) + 1
                                    : 1;
                                toppings.push({
                                  id: nextId,
                                  name: "",
                                  value: 0,
                                });
                                this.handleModalDataChange(toppings, "topping");
                              }}
                              onChange={(val, type, id, field) => {
                                const toppings = (
                                  this.state.editingData.topping || []
                                ).map((item) =>
                                  item.id === id
                                    ? {
                                        ...item,
                                        [field]:
                                          field === "value"
                                            ? val.toString().replace(/,/g, "")
                                            : val,
                                      }
                                    : item,
                                );
                                this.handleModalDataChange(toppings, "topping");
                              }}
                              onRemove={(type, id) => {
                                const toppings = (
                                  this.state.editingData.topping || []
                                ).filter((item) => item.id !== id);
                                this.handleModalDataChange(toppings, "topping");
                              }}
                              name="topping"
                              values={this.state.editingData.topping || []}
                            />
                          </FormGroup>
                        </Colxx>
                        {business_type === "cafe" && (
                          <Colxx md="6">
                            <FormGroup className="mb-4">
                              <Label className="text-muted">
                                {this.state.editingData.iceTitle || "Lượng đá"}
                              </Label>
                              <IceAndSugar
                                selectedValue={this.state.editingData.ice || []}
                                onSelected={({ name, value }) => {
                                  const current =
                                    this.state.editingData.ice || [];
                                  const next = current.includes(value)
                                    ? current.filter((x) => x !== value)
                                    : [...current, value];
                                  this.handleModalDataChange(next, "ice");
                                }}
                                name="ice"
                              />
                            </FormGroup>
                            <FormGroup className="mb-4">
                              <Label className="text-muted">
                                {this.state.editingData.sugarTitle || "Độ ngọt"}
                              </Label>
                              <IceAndSugar
                                selectedValue={
                                  this.state.editingData.sugar || []
                                }
                                onSelected={({ name, value }) => {
                                  const current =
                                    this.state.editingData.sugar || [];
                                  const next = current.includes(value)
                                    ? current.filter((x) => x !== value)
                                    : [...current, value];
                                  this.handleModalDataChange(next, "sugar");
                                }}
                                name="sugar"
                              />
                            </FormGroup>
                          </Colxx>
                        )}
                      </Row>
                    ) : (
                      <Row>
                        <Colxx md="12">
                          <Row>
                            <Colxx md="4">
                              <FormGroup>
                                <Label>Size / Kích thước</Label>
                                <Creatable
                                  isMulti
                                  value={(
                                    this.state.editingData.size || []
                                  ).map((item) =>
                                    typeof item === "string"
                                      ? { label: item, value: item }
                                      : item,
                                  )}
                                  onChange={(val) =>
                                    this.handleModalDataChange(val, "size")
                                  }
                                  placeholder="Chọn..."
                                />
                              </FormGroup>
                            </Colxx>
                            {business_type === "fashion" && (
                              <Colxx md="4">
                                <FormGroup>
                                  <Label>Màu sắc</Label>
                                  <Creatable
                                    isMulti
                                    value={(
                                      this.state.editingData.color || []
                                    ).map((item) =>
                                      typeof item === "string"
                                        ? { label: item, value: item }
                                        : item,
                                    )}
                                    onChange={(val) =>
                                      this.handleModalDataChange(val, "color")
                                    }
                                    placeholder="Chọn..."
                                  />
                                </FormGroup>
                              </Colxx>
                            )}
                            {(business_type === "fashion" ||
                              business_type === "retail") && (
                              <Colxx md="4">
                                <FormGroup>
                                  <Label>Chất liệu</Label>
                                  <Creatable
                                    isMulti
                                    value={(
                                      this.state.editingData.material || []
                                    ).map((item) =>
                                      typeof item === "string"
                                        ? { label: item, value: item }
                                        : item,
                                    )}
                                    onChange={(val) =>
                                      this.handleModalDataChange(
                                        val,
                                        "material",
                                      )
                                    }
                                    placeholder="Chọn..."
                                  />
                                </FormGroup>
                              </Colxx>
                            )}
                          </Row>
                          <Button
                            size="sm"
                            outline
                            color="info"
                            className="mb-3"
                            onClick={this.handleModalGenerateVariants}
                          >
                            Tạo biến thể tự động
                          </Button>

                          {/* Master Settings */}
                          {(this.state.editingData.variant || []).length >
                            0 && (
                            <Card className="mb-4 bg-light">
                              <CardBody className="p-3">
                                <h6>Áp dụng hàng loạt</h6>
                                <Row>
                                  <Colxx xs="6" md="3">
                                    <FormGroup>
                                      <Label>Giá bán chung</Label>
                                      <PriceInput
                                        value={this.state.masterVariantSettings.price}
                                        name="price"
                                        onChange={(val, name) =>
                                          this.handleModalMasterSettingsChange(val, name)
                                        }
                                      />
                                    </FormGroup>
                                  </Colxx>
                                  <Colxx xs="6" md="3">
                                    <FormGroup>
                                      <Label>Giá KM chung</Label>
                                      <PriceInput
                                        value={this.state.masterVariantSettings.promo_price}
                                        name="promo_price"
                                        onChange={(val, name) =>
                                          this.handleModalMasterSettingsChange(val, name)
                                        }
                                      />
                                    </FormGroup>
                                  </Colxx>
                                  <Colxx xs="6" md="3">
                                    <FormGroup>
                                      <Label>Số lượng chung</Label>
                                      <PriceInput
                                        value={this.state.masterVariantSettings.quantity}
                                        name="quantity"
                                        onChange={(val, name) =>
                                          this.handleModalMasterSettingsChange(val, name)
                                        }
                                      />
                                    </FormGroup>
                                  </Colxx>
                                  <Colxx
                                    xs="6"
                                    md="3"
                                    className="d-flex align-items-end"
                                  >
                                    <Button
                                      color="info"
                                      size="sm"
                                      className="mb-3"
                                      onClick={this.applyMasterSettingsToAll}
                                    >
                                      Áp dụng
                                    </Button>
                                  </Colxx>
                                </Row>
                              </CardBody>
                            </Card>
                          )}

                          {/* Variants List */}
                          {(this.state.editingData.variant || []).map(
                            (v, idx) => (
                              <Card key={v.id} className="mb-2">
                                <CardBody className="p-2">
                                  <div
                                    className="d-flex justify-content-between align-items-center cursor-pointer"
                                    onClick={() =>
                                      this.toggleAccordionVariations(v.id)
                                    }
                                  >
                                    <strong>{v.name}</strong>
                                    <div>
                                      <Badge color="light" className="mr-2">
                                        {AddCommaNumber(v.price)}đ
                                      </Badge>
                                      <i
                                        className={`simple-icon-arrow-${v.isOpen ? "up" : "down"}`}
                                      />
                                      <Button
                                        close
                                        size="sm"
                                        className="ml-2 text-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          this.handleModalDeleteVariant(v.id);
                                        }}
                                      >
                                        ×
                                      </Button>
                                    </div>
                                  </div>
                                  <Collapse isOpen={v.isOpen}>
                                    <div className="pt-3 border-top mt-2">
                                      <Row>
                                        <Colxx md="4">
                                          <FormGroup>
                                            <Label>Giá bán</Label>
                                            <PriceInput
                                              value={v.price}
                                              onChange={(val) =>
                                                this.handleModalVariantChange(
                                                  val,
                                                  v.id,
                                                  "price",
                                                )
                                              }
                                            />
                                          </FormGroup>
                                        </Colxx>
                                        <Colxx md="4">
                                          <FormGroup>
                                            <Label>Giá KM</Label>
                                            <PriceInput
                                              value={v.promo_price}
                                              onChange={(val) =>
                                                this.handleModalVariantChange(
                                                  val,
                                                  v.id,
                                                  "promo_price",
                                                )
                                              }
                                            />
                                          </FormGroup>
                                        </Colxx>
                                        <Colxx md="4">
                                          <FormGroup>
                                            <Label>Số lượng</Label>
                                            <PriceInput
                                              value={v.quantity}
                                              onChange={(val) =>
                                                this.handleModalVariantChange(
                                                  val,
                                                  v.id,
                                                  "quantity",
                                                )
                                              }
                                            />
                                          </FormGroup>
                                        </Colxx>
                                      </Row>
                                    </div>
                                  </Collapse>
                                </CardBody>
                              </Card>
                            ),
                          )}
                        </Colxx>
                      </Row>
                    )}
                  </Colxx>
                </Row>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.saveModalChanges}>
              {messages["general.done"] || "Lưu"}
            </Button>
            <Button color="secondary" onClick={this.toggleVariantModal}>
              {messages["general.btn-cancel"] || "Hủy"}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

export default injectIntl(ContentMultiForm);
